// Screen 6 (spec 13 §Screens): data entry for hours, managers and operators. The focused week
// (`p`, the shell's week chip) is the form's week: DE seat × person × kind × day → minutes, saved
// as one `time_entry` row (`POST /time-entries`, grain day; the server stamps `period_key`).
// Three calls on load: the DEs (their seats), the people, and the week's entries of every seat.
// The week's days come from the company calendar on `/v1/me` (review #7); the seat and person
// pickers are searchable and follow the lists' cursors (review #8). Each entry has a row menu:
// edit, approve or reject, void (de/time-entry-actions.tsx); "Needs approval" filters the loaded
// entries to the pending ones (coverage rows 66, 67).
//
// Founder time is agency-visibility (13 §Principals): only agency operators get those rows from
// the API and only they are offered the "Founder" kind. Nothing is fetched and hidden.
import {
  Badge,
  Button,
  EmptyState,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  PageHeader,
  SectionHeading,
  Segmented,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  workerName,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClockIcon, PlusIcon } from "lucide-react";
import { type FormEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { TimeEntry } from "../../api/types";
import type { CompanyAccess } from "../company";
import { TimeEntryActions } from "../de/time-entry-actions";
import { ApprovalBadge } from "../de/time-tab";
import { formatDate, formatMinutes, TIME_KIND_LABELS } from "../format";
import { PagedPicker, type PickerOption } from "../paged-picker";
import { digitalEmployeesQuery, keys, peopleQuery, weekTimeQuery } from "../queries";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { companyWeekDays } from "./calendar";

type Kind = TimeEntry["kind"];
const CLIENT_KINDS: Kind[] = ["supervision", "work", "setup", "other"];

export function HoursForm({
  api,
  company,
  week,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
}) {
  const operator = company.roles.includes("agency_operator");
  const kinds: Kind[] = operator ? [...CLIENT_KINDS, "founder"] : CLIENT_KINDS;
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const people = useInfiniteQuery(peopleQuery(api, company.id));
  const entries = useInfiniteQuery(weekTimeQuery(api, company.id, week));
  // "Needs approval" (coverage row 66): the loaded entries still pending a decision.
  const [filter, setFilter] = useState<"all" | "pending">("all");

  const seats = (des.data?.pages.flatMap((p) => p.data) ?? []).flatMap((d) =>
    d.seat ? [{ id: d.seat.id, label: `${workerName(d.display_name)} · ${d.seat.title}` }] : [],
  );
  const persons = people.data?.pages.flatMap((p) => p.data) ?? [];
  // The week's days in the company's calendar (its week start and time zone; review #7).
  const days = companyWeekDays(company.calendar, week);
  const range = days.length
    ? `${formatDate(days[0] ?? "")} – ${formatDate(days.at(-1) ?? "")}`
    : "";

  return (
    <>
      <PageHeader
        eyebrow="Data entry"
        title={`Hours · ${week}`}
        subtitle={`${range}. Change the week with the week picker in the top bar.`}
      />
      {des.isPending || people.isPending ? (
        <Loading label="Loading the hours form" rows={2} />
      ) : des.isError ? (
        <LoadError what="Digital employees" error={des.error} />
      ) : people.isError ? (
        <LoadError what="People" error={people.error} />
      ) : !seats.length ? (
        <EmptyState
          variant="card"
          icon={ClockIcon}
          title="No DE seats yet"
          description="Hours are logged against a DE seat. Set up a digital employee first."
        />
      ) : (
        <EntryForm
          api={api}
          co={company.id}
          week={week}
          seats={pagedOf(des, seats)}
          persons={pagedOf(
            people,
            // Time is logged for active people only (the API refuses anyone else).
            persons
              .filter((p) => p.status === "active")
              .map((p) => ({ id: p.id, label: p.display_name })),
          )}
          kinds={kinds}
          days={days}
        />
      )}
      <section aria-labelledby="hours-week" className="mt-8 flex min-w-0 flex-col gap-3">
        <SectionHeading
          headingId="hours-week"
          title="This week's entries"
          description="Edit, approve, reject or void an entry from its row menu."
          actions={
            <Segmented
              aria-label="Show entries"
              size="sm"
              value={filter}
              onValueChange={setFilter}
              options={[
                { value: "all", label: "All" },
                { value: "pending", label: "Needs approval" },
              ]}
            />
          }
          className="mb-1"
        />
        {entries.isPending ? (
          <Loading label="Loading time entries" rows={3} />
        ) : entries.isError ? (
          <LoadError what="Time entries" error={entries.error} />
        ) : (
          <EntriesTable
            api={api}
            co={company.id}
            filter={filter}
            rows={entries.data.pages.flatMap((p) => p.data)}
            partial={entries.hasNextPage}
            week={week}
            seatName={(id) => seats.find((s) => s.id === id)?.label ?? `Seat …${id.slice(-4)}`}
            personName={(id) =>
              persons.find((p) => p.id === id)?.display_name ?? `Member …${id.slice(-4)}`
            }
          />
        )}
        <LoadMore
          what="time entries"
          hasMore={entries.hasNextPage}
          loading={entries.isFetchingNextPage}
          onMore={() => void entries.fetchNextPage()}
        />
      </section>
    </>
  );
}

/** A picker's paged options (`PagedPicker` props). */
type Paged = {
  options: PickerOption[];
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
};

const pagedOf = (
  q: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown },
  options: PickerOption[],
): Paged => ({
  options,
  hasMore: q.hasNextPage,
  loading: q.isFetchingNextPage,
  onMore: () => void q.fetchNextPage(),
});

function EntryForm({
  api,
  co,
  week,
  seats,
  persons,
  kinds,
  days,
}: {
  api: Api;
  co: string;
  week: string;
  seats: Paged;
  persons: Paged;
  kinds: Kind[];
  days: string[];
}) {
  const qc = useQueryClient();
  const [seat, setSeat] = useState("");
  const [person, setPerson] = useState("");
  const [kind, setKind] = useState<Kind>("supervision");
  const [day, setDay] = useState(days[0] ?? "");
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const value = Number(minutes);
  // The chosen day stays inside the form's week when the week changes.
  const dayValue = days.includes(day) ? day : (days[0] ?? "");
  // Until one is chosen, the first loaded option is the choice.
  const seatValue = seat || seats.options[0]?.id || "";
  const personValue = person || persons.options[0]?.id || "";
  const valid = !!seatValue && !!personValue && !!dayValue && Number.isInteger(value) && value > 0;
  const save = useMutation({
    mutationFn: () =>
      api.client.timeEntries.create({
        params: { co },
        body: {
          seat_id: seatValue,
          membership_id: personValue,
          kind,
          grain: "day",
          work_date: dayValue,
          duration_seconds: value * 60,
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: (row) => {
      setStatus(
        `Saved ${formatMinutes(row.minutes)} of ${TIME_KIND_LABELS[row.kind]?.toLowerCase() ?? row.kind} for ${row.period_key}.`,
      );
      setMinutes("");
      setNote("");
      void qc.invalidateQueries({ queryKey: keys.weekTime(co, week) });
    },
  });
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) save.mutate();
  };
  return (
    <form onSubmit={onSubmit} aria-label="Add hours" className="max-w-4xl">
      <SettingsSection icon={PlusIcon} title="Add hours">
        <FieldGroup>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <PagedPicker
              id="hours-seat"
              label="DE seat"
              value={seatValue}
              onChange={setSeat}
              {...seats}
            />
            <PagedPicker
              id="hours-person"
              label="Person"
              value={personValue}
              onChange={setPerson}
              {...persons}
            />
            <Field>
              <FieldLabel htmlFor="hours-kind">Kind</FieldLabel>
              <NativeSelect
                id="hours-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as Kind)}
              >
                {kinds.map((k) => (
                  <NativeSelectOption key={k} value={k}>
                    {TIME_KIND_LABELS[k]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor="hours-day">Day</FieldLabel>
              <NativeSelect
                id="hours-day"
                value={dayValue}
                onChange={(e) => setDay(e.target.value)}
              >
                {days.map((d) => (
                  <NativeSelectOption key={d} value={d}>
                    {new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      timeZone: "UTC",
                    })}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field>
              <FieldLabel htmlFor="hours-minutes">Minutes</FieldLabel>
              <Input
                id="hours-minutes"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                required
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="hours-note">Note</FieldLabel>
              <Input id="hours-note" value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" size="lg" disabled={!valid || save.isPending}>
              {save.isPending ? "Saving…" : "Save hours"}
            </Button>
            <span role="status" className="text-sm text-muted-foreground">
              {status}
            </span>
          </div>
        </FieldGroup>
      </SettingsSection>
    </form>
  );
}

function EntriesTable({
  api,
  co,
  filter,
  rows: all,
  partial,
  week,
  seatName,
  personName,
}: {
  api: Api;
  co: string;
  filter: "all" | "pending";
  rows: TimeEntry[];
  partial: boolean;
  week: string;
  seatName: (id: string) => string;
  personName: (id: string) => string;
}) {
  const rows = filter === "pending" ? all.filter((r) => r.approval_status === "pending") : all;
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={ClockIcon}
        title={
          filter === "pending" ? `Nothing needs approval in ${week}` : `No hours in ${week} yet`
        }
        description={filter === "pending" ? undefined : "Saved entries appear here."}
      />
    );
  const byKind = new Map<string, number>();
  for (const r of rows) byKind.set(r.kind, (byKind.get(r.kind) ?? 0) + r.minutes);
  const total = rows.reduce((s, r) => s + r.minutes, 0);
  const suffix = partial ? " (loaded entries only)" : "";
  return (
    <Table data-slot="hours-entries">
      <TableCaption>Time entries of week {week}, every seat.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Date</TableHead>
          <TableHead scope="col">Seat</TableHead>
          <TableHead scope="col">Person</TableHead>
          <TableHead scope="col">Kind</TableHead>
          <TableHead scope="col" className="text-right">
            Minutes
          </TableHead>
          <TableHead scope="col">Period</TableHead>
          <TableHead scope="col">Approval</TableHead>
          <TableHead scope="col">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.id} className="group/row" data-kind={r.kind} data-period={r.period_key}>
            <TableCell>{formatDate(r.work_date)}</TableCell>
            <TableHead scope="row" className="font-normal">
              {seatName(r.seat_id)}
            </TableHead>
            <TableCell>{personName(r.membership_id)}</TableCell>
            <TableCell>
              {TIME_KIND_LABELS[r.kind] ?? r.kind}
              {r.visibility === "agency" ? (
                <Badge variant="secondary" className="ml-1.5">
                  Agency
                </Badge>
              ) : null}
            </TableCell>
            <TableCell className="text-right tabular-nums">{r.minutes}</TableCell>
            <TableCell className="font-mono text-xs">{r.period_key}</TableCell>
            <TableCell>
              <ApprovalBadge entry={r} />
            </TableCell>
            <TableCell className="text-right">
              <TimeEntryActions
                api={api}
                companyId={co}
                entry={r}
                who={personName(r.membership_id)}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
      <TableFooter>
        {[...byKind].map(([kind, minutes]) => (
          <TableRow key={kind}>
            <TableHead scope="row" colSpan={4}>
              Total {TIME_KIND_LABELS[kind]?.toLowerCase() ?? kind}
              {suffix}
            </TableHead>
            <TableCell className="text-right tabular-nums" data-total={kind}>
              {formatMinutes(minutes)}
            </TableCell>
            <TableCell colSpan={3} />
          </TableRow>
        ))}
        <TableRow>
          <TableHead scope="row" colSpan={4}>
            Total{suffix}
          </TableHead>
          <TableCell className="text-right tabular-nums" data-total="all">
            {formatMinutes(total)}
          </TableCell>
          <TableCell colSpan={2} />
        </TableRow>
      </TableFooter>
    </Table>
  );
}
